import { logError } from '@/utils/logger';
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash, MagnifyingGlass, UserGear, MapPin, Phone } from '@phosphor-icons/react';
import SearchableSelect from '@/components/SearchableSelect';
import PhotoUpload from '@/components/PhotoUpload';
import ContactActions from '@/components/ContactActions';

const emptyForm = { name: '', phone: '', email: '', photo_url: null, zone_id: '', sites: [], notes: '' };

export default function Supervisors() {
  const [supervisors, setSupervisors] = useState([]);
  const [zones, setZones] = useState([]);
  const [clients, setClients] = useState([]);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [siteSearch, setSiteSearch] = useState('');

  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchAll = useCallback(async () => {
    try {
      const [supRes, zonesRes, clientsRes, meRes] = await Promise.all([
        fetch(`${API}/api/supervisors`, { credentials: 'include' }),
        fetch(`${API}/api/zones`, { credentials: 'include' }),
        fetch(`${API}/api/clients`, { credentials: 'include' }),
        fetch(`${API}/api/auth/me`, { credentials: 'include' }),
      ]);
      if (supRes.ok) setSupervisors(await supRes.json());
      if (zonesRes.ok) setZones(await zonesRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
      if (meRes.ok) setUserRole((await meRes.json()).role);
    } catch (error) { logError('Fetch supervisors:', error); }
    finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const allSites = clients.flatMap(c => (c.sites || []).map(s => ({ site: s, client: c.name })));
  const canEdit = ['admin', 'drh', 'supervisor'].includes(userRole);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editing ? `${API}/api/supervisors/${editing.supervisor_id}` : `${API}/api/supervisors`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData),
      });
      if (res.ok) {
        toast.success(editing ? 'Superviseur modifié' : 'Superviseur enregistré');
        setShowDialog(false); setEditing(null); setFormData(emptyForm);
        fetchAll();
      } else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Déplacer ce superviseur vers la corbeille ?')) return;
    try {
      const res = await fetch(`${API}/api/supervisors/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Déplacé vers la corbeille'); fetchAll(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const toggleSite = (site) => {
    const sites = formData.sites.includes(site) ? formData.sites.filter(s => s !== site) : [...formData.sites, site];
    setFormData({ ...formData, sites });
  };

  const getZoneName = (id) => zones.find(z => z.zone_id === id)?.name || '-';

  const filtered = supervisors.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return s.name?.toLowerCase().includes(q) || s.phone?.includes(q) || (s.sites || []).some(site => site.toLowerCase().includes(q));
  });

  if (loading) return <div className="text-[#94A3B8]">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="supervisors-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#F8FAFC] tracking-tight">Superviseurs de Sites</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">Superviseurs travaillant avec les inspecteurs sur le terrain</p>
        </div>
        {canEdit && (
          <Button onClick={() => { setEditing(null); setFormData(emptyForm); setShowDialog(true); }} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="add-supervisor-button">
            <Plus size={18} className="mr-2" /> Nouveau superviseur
          </Button>
        )}
      </div>

      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par nom, téléphone ou site..." className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] pl-9" data-testid="search-supervisor" />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-12 text-center">
          <UserGear size={44} className="mx-auto text-[#334155] mb-3" weight="duotone" />
          <p className="text-[#94A3B8]">Aucun superviseur enregistré</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(sup => (
            <div key={sup.supervisor_id} className="bg-[#0F172A] border border-[#1E293B] rounded-md p-5 space-y-3 hover:border-[#2563EB]/40 transition-colors" data-testid={`supervisor-card-${sup.supervisor_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  {sup.photo_url ? (
                    <img src={sup.photo_url} alt={sup.name} className="h-11 w-11 rounded-full object-cover border border-[#334155]" />
                  ) : (
                    <div className="h-11 w-11 rounded-full bg-[#F59E0B] flex items-center justify-center text-white font-medium">{sup.name?.charAt(0)}</div>
                  )}
                  <div className="min-w-0">
                    <p className="text-[#F8FAFC] font-semibold truncate">{sup.name}</p>
                    <p className="text-xs text-[#94A3B8] flex items-center gap-1"><Phone size={11} /> {sup.phone || '-'}</p>
                    {sup.phone && <div className="mt-1"><ContactActions phone={sup.phone} name={sup.name} size="xs" /></div>}
                  </div>
                </div>
                {canEdit && (
                  <div className="flex flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(sup); setFormData({ ...emptyForm, ...sup }); setShowDialog(true); }} className="text-[#2563EB] hover:text-white hover:bg-[#2563EB] h-8 w-8 p-0"><Pencil size={15} /></Button>
                    {userRole === 'admin' && (
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(sup.supervisor_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444] h-8 w-8 p-0"><Trash size={15} /></Button>
                    )}
                  </div>
                )}
              </div>
              <Badge className="bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30 text-xs"><MapPin size={11} className="mr-1" />{getZoneName(sup.zone_id)}</Badge>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1.5">Sites supervisés ({(sup.sites || []).length})</p>
                {(sup.sites || []).length === 0 ? (
                  <p className="text-xs text-[#64748B]">Aucun site confié</p>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {sup.sites.map((s) => <Badge key={s} className="bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30 text-xs">{s}</Badge>)}
                  </div>
                )}
              </div>
              {sup.notes && <p className="text-xs text-[#94A3B8] italic">{sup.notes}</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold">{editing ? 'Modifier le superviseur' : 'Nouveau superviseur de sites'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex justify-center">
              <PhotoUpload currentUrl={formData.photo_url} onUploaded={(url) => setFormData({ ...formData, photo_url: url })} size="lg" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#F8FAFC]">Nom complet *</Label>
                <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="supervisor-name" />
              </div>
              <div>
                <Label className="text-[#F8FAFC]">Téléphone</Label>
                <Input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="supervisor-phone" />
              </div>
              <div>
                <Label className="text-[#F8FAFC]">Email</Label>
                <Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
              </div>
              <div>
                <Label className="text-[#F8FAFC]">Zone *</Label>
                <div className="mt-1">
                  <SearchableSelect value={formData.zone_id} onValueChange={(v) => setFormData({ ...formData, zone_id: v })} options={zones.map(z => ({ value: z.zone_id, label: z.name }))} placeholder="Choisir la zone" testId="supervisor-zone-select" />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-[#F8FAFC]">Sites à superviser ({formData.sites.length} sélectionné(s))</Label>
              <div className="relative mt-1 mb-2">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <Input value={siteSearch} onChange={e => setSiteSearch(e.target.value)} placeholder="Rechercher un site..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC] pl-9 h-9" data-testid="supervisor-site-search" />
              </div>
              <div className="max-h-[180px] overflow-y-auto space-y-1 p-2 bg-[#1E293B]/30 rounded-md border border-[#334155]">
                {allSites.filter(({ site, client }) => !siteSearch || site.toLowerCase().includes(siteSearch.toLowerCase()) || client.toLowerCase().includes(siteSearch.toLowerCase())).map(({ site, client }) => (
                  <button key={`${client}-${site}`} type="button" onClick={() => toggleSite(site)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm text-left transition-colors ${formData.sites.includes(site) ? 'bg-[#2563EB]/20 border border-[#2563EB]/50 text-[#F8FAFC]' : 'text-[#94A3B8] hover:bg-[#1E293B] border border-transparent'}`}
                    data-testid={`toggle-site-${site}`}>
                    <span className="truncate">{site}</span>
                    <span className="text-xs text-[#64748B] flex-shrink-0 ml-2">{client}</span>
                  </button>
                ))}
                {allSites.length === 0 && <p className="text-xs text-[#64748B] p-2">Aucun site client enregistré</p>}
              </div>
            </div>
            <div>
              <Label className="text-[#F8FAFC]">Notes</Label>
              <Input value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Remarques..." className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
              <Button type="submit" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="save-supervisor-button">Enregistrer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
